/**
 * Usage data for the AI (ai_request and ai_set_up in shared/usage.ts), sent only while sharing is on: which feature
 * asked, the provider, and how it ended. Nothing of the exchange itself: no key, no prompt, no answer. The AI simulator
 * (dev builds) isn't counted: it's no provider anyone uses.
 */
import { isAiProviderId } from '@shared/ai';
import { USAGE_AI_FEATURES, USAGE_AI_MODELS, USAGE_AI_OUTCOMES, type UsageAiFeature, type UsageAiModel, type UsageAiOutcome } from '@shared/usage';
import { track } from '@/lib/usage';
import type { AiClient, AiConfig, AiErrorKind } from './types';

// Every kind of AI failure is one of the outcomes the event may carry: a new kind fails typecheck until it's added.
const knownKinds: Record<Exclude<AiErrorKind, UsageAiOutcome>, never> = {};
void knownKinds;

/** A structured request's feature, by the schema it asks for (schemaName). Null for one the registry doesn't know. */
export function aiFeatureOf(schemaName: string): UsageAiFeature | null {
  return (USAGE_AI_FEATURES as readonly string[]).includes(schemaName) ? (schemaName as UsageAiFeature) : null;
}

export function aiOutcomeOf(error: unknown): UsageAiOutcome {
  if (error instanceof Error && error.name === 'AbortError') return 'stopped';
  // An AiError, or a ServerAiError (server.ts, not imported here so the providers' code stays out of the main chunk).
  const kind = error instanceof Error && 'kind' in error ? error.kind : null;
  if (typeof kind === 'string' && (USAGE_AI_OUTCOMES as readonly string[]).includes(kind)) return kind as UsageAiOutcome;
  return 'other';
}

/** The model, when it is one Taretto was evaluated with. */
export function aiModelOf(model: string): UsageAiModel {
  return (USAGE_AI_MODELS as readonly string[]).includes(model) ? (model as UsageAiModel) : 'other';
}

function watched<T>(feature: UsageAiFeature | null, config: AiConfig, request: Promise<T>): Promise<T> {
  const provider = config.provider;
  if (!feature || !isAiProviderId(provider)) return request;
  const via = config.server ? 'server' : 'device';
  const report = (outcome: UsageAiOutcome) => track('ai_request', { feature, provider, via, outcome });
  return request.then(
    (value) => {
      report('answered');
      return value;
    },
    (error: unknown) => {
      report(aiOutcomeOf(error));
      throw error;
    },
  );
}

/** `client`, counting each request it makes. */
export function withUsage(client: AiClient): AiClient {
  return {
    ...client,
    testKey: (config) => watched('test_key', config, client.testKey(config)),
    structured: (config, request) => watched(aiFeatureOf(request.schemaName), config, client.structured(config, request)),
    chat: (config, request) => watched('ask', config, client.chat(config, request)),
  };
}

/** AI saved in Settings › AI on this device. */
export function trackAiSetUp(config: AiConfig): void {
  if (isAiProviderId(config.provider)) track('ai_set_up', { provider: config.provider, model: aiModelOf(config.model) });
}
